import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  Calendar,
  Briefcase,
  Shield,
  RefreshCw,
  Download,
  Upload,
  FileSpreadsheet,
  FileText,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Weekdays configuration
const WEEKDAYS = [
  { key: 'monday', label: 'Montag', short: 'Mo' },
  { key: 'tuesday', label: 'Dienstag', short: 'Di' },
  { key: 'wednesday', label: 'Mittwoch', short: 'Mi' },
  { key: 'thursday', label: 'Donnerstag', short: 'Do' },
  { key: 'friday', label: 'Freitag', short: 'Fr' },
  { key: 'saturday', label: 'Samstag', short: 'Sa' },
  { key: 'sunday', label: 'Sonntag', short: 'So' }
];

const Employees = () => {
  const { permissions } = usePermissions();
  const canEdit = permissions?.employees_edit || false;
  const canDelete = permissions?.employees_delete || false;
  
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_number: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    work_area: '',
    role: 'techniker',
    hire_date: new Date().toISOString().split('T')[0],
    qualifications: [],
    work_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], // Default Mo-Fr
    annual_vacation_days: 30 // Default 30 Urlaubstage
  });
  const [submitting, setSubmitting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Helper functions for work days
  const toggleWorkDay = (day) => {
    setFormData(prev => ({
      ...prev,
      work_days: prev.work_days.includes(day)
        ? prev.work_days.filter(d => d !== day)
        : [...prev.work_days, day]
    }));
  };

  const setAllWorkDays = (enabled) => {
    setFormData(prev => ({
      ...prev,
      work_days: enabled ? WEEKDAYS.map(w => w.key) : []
    }));
  };

  const setWeekdaysOnly = () => {
    setFormData(prev => ({
      ...prev,
      work_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    }));
  };

  const formatWorkDays = (workDays) => {
    if (!workDays || workDays.length === 0) return 'Keine Arbeitstage';
    return WEEKDAYS
      .filter(w => workDays.includes(w.key))
      .map(w => w.short)
      .join(', ');
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Fehler beim Laden der Mitarbeiter');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Ensure annual_vacation_days is a number
      const submitData = {
        ...formData,
        annual_vacation_days: parseInt(formData.annual_vacation_days) || 30
      };
      
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, submitData);
        toast.success('Mitarbeiter erfolgreich aktualisiert');
        setShowEditDialog(false);
      } else {
        await axios.post(`${API}/employees`, submitData);
        toast.success('Mitarbeiter erfolgreich erstellt');
        setShowAddDialog(false);
      }
      
      fetchEmployees();
      resetForm();
    } catch (error) {
      console.error('Error saving employee:', error);
      
      let errorMessage = 'Fehler beim Speichern des Mitarbeiters';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(err => {
            const field = err.loc?.[err.loc.length - 1] || 'Unbekanntes Feld';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = 'Validierungsfehler beim Speichern';
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (employee) => {
    if (!canEdit) {
      toast.error('Diese Funktion ist nur für Administratoren verfügbar');
      return;
    }
    
    setEditingEmployee(employee);
    setFormData({
      employee_number: employee.employee_number,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone: employee.phone || '',
      work_area: employee.work_area,
      role: employee.role,
      hire_date: employee.hire_date,
      qualifications: employee.qualifications || [],
      work_days: employee.work_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      annual_vacation_days: employee.annual_vacation_days || 30
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (employeeId, employeeName) => {
    if (!canDelete) {
      toast.error('Diese Funktion ist nur für Administratoren verfügbar');
      return;
    }
    
    if (window.confirm(`Sind Sie sicher, dass Sie ${employeeName} löschen möchten?`)) {
      try {
        await axios.delete(`${API}/employees/${employeeId}`);
        toast.success('Mitarbeiter erfolgreich gelöscht');
        fetchEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        if (error.response?.status === 403) {
          toast.error('Diese Funktion ist nur für Administratoren verfügbar');
        } else {
          toast.error('Fehler beim Löschen des Mitarbeiters');
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employee_number: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      work_area: '',
      role: 'techniker',
      hire_date: new Date().toISOString().split('T')[0],
      qualifications: [],
      work_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], // Default Mo-Fr
      annual_vacation_days: 30 // Default 30 Urlaubstage
    });
    setEditingEmployee(null);
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/employees/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mitarbeiter_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel-Export erfolgreich');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Fehler beim Excel-Export');
    }
  };

  const exportPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/employees/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mitarbeiter_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF-Export erfolgreich');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Fehler beim PDF-Export');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API}/employees/import/excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Import erfolgreich: ${response.data.imported} erstellt, ${response.data.updated} aktualisiert`);
      if (response.data.errors && response.data.errors.length > 0) {
        console.log('Import-Fehler:', response.data.errors);
        toast.warning(`${response.data.errors.length} Fehler beim Import`);
      }
      
      setShowImportDialog(false);
      setImportFile(null);
      fetchEmployees();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Fehler beim Import: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const getStatusBadge = (status) => {
    const variants = {
      aktiv: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      inaktiv: 'bg-red-100 text-red-800 border-red-300',
      krank: 'bg-amber-100 text-amber-800 border-amber-300',
      urlaub: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return variants[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'bg-purple-100 text-purple-800 border-purple-300',
      manager: 'bg-blue-100 text-blue-800 border-blue-300',
      techniker: 'bg-green-100 text-green-800 border-green-300'
    };
    return variants[role] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const filteredEmployees = employees.filter(employee =>
    employee.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.employee_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Lade Mitarbeiter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="employees-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Users className="w-8 h-8 text-emerald-600" />
              Mitarbeiter 👷
            </h1>
            <p className="text-slate-600 mt-1">Verwalten Sie Ihre Mitarbeiter und deren Informationen</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={exportExcel}
              variant="outline"
              className="gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel Export
            </Button>
            <Button 
              onClick={exportPDF}
              variant="outline"
              className="gap-2"
            >
              <FileText className="w-4 h-4" />
              PDF Export
            </Button>
            <Button 
              onClick={() => setShowImportDialog(true)}
              variant="outline"
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              Import
            </Button>
          </div>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => resetForm()}
                data-testid="add-employee-button"
              >
                <Plus className="w-4 h-4" />
                Mitarbeiter hinzufügen
              </Button>
            </DialogTrigger>
            <EmployeeForm
              formData={formData}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
              submitting={submitting}
              isEdit={false}
              formatWorkDays={formatWorkDays}
              weekdays={WEEKDAYS}
            />
          </Dialog>
        </div>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mitarbeiter importieren</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Laden Sie eine Excel-Datei (.xlsx) mit Mitarbeiterdaten hoch. 
                Die Datei sollte folgende Spalten enthalten: Mitarbeiternummer, Vorname, Nachname, E-Mail, Telefon, Rolle, Status, Urlaubsanspruch, Eintrittsdatum.
              </p>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
              {importFile && (
                <p className="text-sm text-emerald-600">
                  Ausgewählt: {importFile.name}
                </p>
              )}
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportFile(null);
                  }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!importFile}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importieren
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Search and Stats */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Mitarbeiter suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="employee-search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={fetchEmployees}
            className="gap-2"
            data-testid="refresh-employees-button"
          >
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Employee Cards */}
      {filteredEmployees.length === 0 ? (
        <Card className="modern-card text-center py-12">
          <CardContent>
            <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine Mitarbeiter gefunden</h3>
            <p className="text-slate-600 mb-4">
              {searchTerm ? 'Keine Mitarbeiter entsprechen Ihrer Suche.' : 'Fügen Sie Ihren ersten Mitarbeiter hinzu.'}
            </p>
            <Button 
              className="gap-2"
              onClick={() => setShowAddDialog(true)}
              data-testid="empty-state-add-button"
            >
              <Plus className="w-4 h-4" />
              Ersten Mitarbeiter hinzufügen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEmployees.map((employee) => (
            <Card key={employee.id} className="modern-card hover:shadow-lg transition-all duration-200">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">
                        {employee.first_name.charAt(0)}{employee.last_name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <CardTitle className="text-lg">
                        {employee.first_name} {employee.last_name}
                      </CardTitle>
                      <p className="text-sm text-slate-500">#{employee.employee_number}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-1">
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(employee)}
                        className="p-2"
                        data-testid={`edit-employee-${employee.id}`}
                        title="Bearbeiten (nur Admin)"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(employee.id, `${employee.first_name} ${employee.last_name}`)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-employee-${employee.id}`}
                        title="Löschen (nur Admin)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {!canEdit && !canDelete && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded text-xs text-slate-500">
                        <Shield className="w-3 h-3" />
                        <span>Nur Ansicht</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Status and Role */}
                <div className="flex items-center gap-2">
                  <Badge className={`${getStatusBadge(employee.status)} border`}>
                    {employee.status}
                  </Badge>
                  <Badge className={`${getRoleBadge(employee.role)} border`}>
                    {employee.role}
                  </Badge>
                </div>

                {/* Contact Info */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{employee.email}</span>
                  </div>
                  {employee.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4" />
                      <span>{employee.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Briefcase className="w-4 h-4" />
                    <span className="capitalize">{employee.work_area}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>Seit {new Date(employee.hire_date).toLocaleDateString('de-DE')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>Arbeitet: {formatWorkDays(employee.work_days)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>Urlaub: {employee.annual_vacation_days || 30} Tage/Jahr</span>
                  </div>
                </div>

                {/* Qualifications */}
                {employee.qualifications && employee.qualifications.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-700 mb-2">Qualifikationen:</p>
                    <div className="flex flex-wrap gap-1">
                      {employee.qualifications.map((qual, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {qual}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <EmployeeForm
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          isEdit={true}
          formatWorkDays={formatWorkDays}
          weekdays={WEEKDAYS}
        />
      </Dialog>
    </div>
  );
};

// Employee Form Component
const EmployeeForm = ({ formData, onInputChange, onSubmit, submitting, isEdit, formatWorkDays, weekdays }) => {
  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>
          {isEdit ? 'Mitarbeiter bearbeiten' : 'Neuen Mitarbeiter hinzufügen'}
        </DialogTitle>
      </DialogHeader>
      
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Mitarbeiter-Nr.</label>
            <Input
              value={formData.employee_number}
              onChange={(e) => onInputChange('employee_number', e.target.value)}
              placeholder="MF001"
              required
              data-testid="employee-number-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Einstellungsdatum</label>
            <Input
              type="date"
              value={formData.hire_date}
              onChange={(e) => onInputChange('hire_date', e.target.value)}
              required
              data-testid="hire-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Urlaubsanspruch (Tage/Jahr)</label>
            <Input
              type="number"
              value={formData.annual_vacation_days}
              onChange={(e) => onInputChange('annual_vacation_days', parseInt(e.target.value) || 30)}
              min="1"
              max="100"
              placeholder="30"
              required
              data-testid="annual-vacation-days-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Anzahl der jährlichen Urlaubstage (Standard: 30)
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Vorname</label>
            <Input
              value={formData.first_name}
              onChange={(e) => onInputChange('first_name', e.target.value)}
              placeholder="Max"
              required
              data-testid="first-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nachname</label>
            <Input
              value={formData.last_name}
              onChange={(e) => onInputChange('last_name', e.target.value)}
              placeholder="Mustermann"
              required
              data-testid="last-name-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">E-Mail</label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => onInputChange('email', e.target.value)}
            placeholder="max@skalartech.com"
            required
            data-testid="email-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Telefon (optional)</label>
          <Input
            value={formData.phone}
            onChange={(e) => onInputChange('phone', e.target.value)}
            placeholder="+49 123 456789"
            data-testid="phone-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Arbeitsbereich</label>
            <Select value={formData.work_area} onValueChange={(value) => onInputChange('work_area', value)}>
              <SelectTrigger data-testid="work-area-select">
                <SelectValue placeholder="Wählen Sie..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="innendienst">Innendienst</SelectItem>
                <SelectItem value="aussendienst">Außendienst</SelectItem>
                <SelectItem value="beides">Beides</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Rolle</label>
            <Select value={formData.role} onValueChange={(value) => onInputChange('role', value)}>
              <SelectTrigger data-testid="role-select">
                <SelectValue placeholder="Wählen Sie..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="techniker">Techniker</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Work Days Section */}
        <div className="space-y-3">
          <label className="block text-sm font-medium">Wochentage (Arbeitszeiten)</label>
          
          {/* Quick Actions */}
          <div className="flex gap-2 mb-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onInputChange('work_days', ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])}
            >
              Mo-Fr
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onInputChange('work_days', weekdays.map(w => w.key))}
            >
              Alle Tage
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onInputChange('work_days', [])}
            >
              Keine
            </Button>
          </div>

          {/* Weekday Toggles */}
          <div className="grid grid-cols-7 gap-2">
            {weekdays.map(weekday => (
              <div key={weekday.key} className="flex flex-col items-center">
                <label className="text-xs font-medium text-slate-600 mb-1">
                  {weekday.short}
                </label>
                <Button
                  type="button"
                  variant={formData.work_days.includes(weekday.key) ? "default" : "outline"}
                  size="sm"
                  className={`w-full h-8 ${
                    formData.work_days.includes(weekday.key) 
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                      : 'hover:bg-emerald-50'
                  }`}
                  onClick={() => {
                    const newWorkDays = formData.work_days.includes(weekday.key)
                      ? formData.work_days.filter(d => d !== weekday.key)
                      : [...formData.work_days, weekday.key];
                    onInputChange('work_days', newWorkDays);
                  }}
                  data-testid={`workday-${weekday.key}`}
                >
                  {formData.work_days.includes(weekday.key) ? '✓' : '○'}
                </Button>
              </div>
            ))}
          </div>

          {/* Selected Days Display */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Ausgewählte Arbeitstage:</strong> {formatWorkDays(formData.work_days)}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={submitting} data-testid="save-employee-button">
            {submitting ? (
              <>
                <div className="loading-spinner mr-2"></div>
                Speichere...
              </>
            ) : (
              isEdit ? 'Aktualisieren' : 'Erstellen'
            )}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default Employees;